import { useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { getFirebaseClient, useAuth, useCrypto, InviteLinkBox } from "@workspace/admin-ui";
import { BookingSettingsSection } from "@/components/BookingSettingsSection";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { PageHeader } from "@/components/AppShell";
import { AdminDialog } from "@workspace/admin-ui";
import { chainAdminApi, type MembershipRow } from "@/lib/callables";
import { useToast } from "@/lib/toast";

export default function PharmacyDetailPage() {
  const { membership, user } = useAuth();
  const chainId = membership?.chainId ?? "";
  const params = useParams<{ pharmacyId: string }>();
  const pharmacyId = params.pharmacyId;
  const { db } = getFirebaseClient();
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showInviteAdmin, setShowInviteAdmin] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [memberFilter, setMemberFilter] = useState<"active" | "suspended">(
    "active",
  );

  const pharmacyQ = useQuery({
    queryKey: ["chain", chainId, "pharmacy", pharmacyId],
    queryFn: async () => {
      const snap = await getDoc(
        doc(db, "chains", chainId, "pharmacies", pharmacyId),
      );
      return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
    },
    enabled: !!chainId,
  });

  const membersQ = useQuery({
    queryKey: ["memberships", chainId, pharmacyId, memberFilter],
    queryFn: () =>
      chainAdminApi.listMemberships({
        chainId,
        pharmacyId,
        status: memberFilter,
      }),
    enabled: !!chainId,
  });

  const pharmaciesQ = useQuery<{ id: string; displayName: string }[]>({
    queryKey: ["chain", chainId, "pharmacies", "transfer-targets"],
    queryFn: async () => {
      const snap = await getDocs(
        query(
          collection(db, "chains", chainId, "pharmacies"),
          orderBy("displayName"),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        displayName: String(
          (d.data() as Record<string, unknown>).displayName ?? d.id,
        ),
      }));
    },
    enabled: !!chainId,
  });
  const transferTargets = (pharmaciesQ.data ?? []).filter(
    (p) => p.id !== pharmacyId,
  );

  const pharmacyAdminsQ = useQuery({
    queryKey: ["pharmacy-admins", chainId, pharmacyId],
    queryFn: () =>
      chainAdminApi.listPharmacyAdmins({ chainId, pharmacyId, status: "all" }),
    enabled: !!chainId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["memberships", chainId, pharmacyId] });
    // Keep the chain-wide Pharmacists tab in sync — it queries the same
    // adminListMemberships data under a separate cache key, so a
    // suspend/reactivate/transfer/revoke/invite here must also invalidate
    // it or that tab shows stale active/inactive status until it
    // independently refetches (see membership cache reconciliation memory
    // note).
    qc.invalidateQueries({ queryKey: ["chain", chainId, "pharmacists"] });
  };

  const invalidateAdmins = () => {
    qc.invalidateQueries({ queryKey: ["pharmacy-admins", chainId, pharmacyId] });
  };

  if (pharmacyQ.isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }
  if (!pharmacyQ.data) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>
          Pharmacy not found.{" "}
          <Typography
            component={Link}
            href="/"
            sx={{ color: "primary.main", textDecoration: "underline" }}
          >
            Back
          </Typography>
        </Typography>
      </Box>
    );
  }
  const p = pharmacyQ.data;

  return (
    <>
      <PageHeader
        title={String(p.displayName ?? pharmacyId)}
        description={`${String(p.address ?? "")} · PSI ${String(p.psiNumber ?? "")} · ${String(p.eircode ?? "")}`}
        backHref="/pharmacies"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowEdit((v) => !v);
                setShowInvite(false);
                setShowInviteAdmin(false);
              }}
            >
              {showEdit ? "Cancel" : "Edit pharmacy"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setShowInviteAdmin((v) => !v);
                setShowInvite(false);
                setShowEdit(false);
              }}
            >
              {showInviteAdmin ? "Cancel" : "Add pharmacy admin"}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowInvite((v) => !v);
                setShowEdit(false);
                setShowInviteAdmin(false);
              }}
            >
              {showInvite ? "Cancel" : "Add pharmacist"}
            </Button>
            <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
              Delete pharmacy
            </Button>
          </Stack>
        }
      />
      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
        {showEdit ? (
          <EditPharmacyForm
            chainId={chainId}
            pharmacyId={pharmacyId}
            initial={{
              name: String(p.displayName ?? ""),
              address: String(p.address ?? ""),
              eircode: String(p.eircode ?? ""),
              psi: String(p.psiNumber ?? ""),
            }}
            onSuccess={() => {
              setShowEdit(false);
              qc.invalidateQueries({
                queryKey: ["chain", chainId, "pharmacy", pharmacyId],
              });
              qc.invalidateQueries({
                queryKey: ["chain", chainId, "pharmacies"],
              });
            }}
          />
        ) : null}

        {showInviteAdmin ? (
          <InvitePharmacyAdminForm
            chainId={chainId}
            pharmacyId={pharmacyId}
            onSuccess={() => {
              setShowInviteAdmin(false);
              invalidateAdmins();
            }}
          />
        ) : null}

        {showInvite ? (
          <InvitePharmacistForm
            chainId={chainId}
            pharmacyId={pharmacyId}
            onSuccess={() => {
              setShowInvite(false);
              invalidate();
            }}
          />
        ) : null}

        <Paper>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Pharmacy Admins
            </Typography>
          </Box>
          {pharmacyAdminsQ.isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Loading…</Typography>
            </Box>
          ) : pharmacyAdminsQ.error ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {(pharmacyAdminsQ.error as Error).message}
              </Typography>
            </Box>
          ) : !pharmacyAdminsQ.data ||
            pharmacyAdminsQ.data.items.filter((m) => m.role === "pharmacy_admin").length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                No pharmacy admins assigned. Use "Add pharmacy admin" to create one.
              </Typography>
            </Box>
          ) : (
            <>
              {pharmacyAdminsQ.data.items
                .filter((m) => m.role === "pharmacy_admin")
                .map((m, i, arr) => (
                  <Box key={m.uid}>
                    <PharmacyAdminRow member={m} chainId={chainId} onChange={invalidateAdmins} />
                    {i < arr.length - 1 && <Divider />}
                  </Box>
                ))}
            </>
          )}
        </Paper>

        <Paper>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Pharmacists
            </Typography>
            <ToggleButtonGroup
              value={memberFilter}
              exclusive
              size="small"
              onChange={(_, v) => {
                if (v) setMemberFilter(v as "active" | "suspended");
              }}
            >
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="suspended">Suspended</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {membersQ.isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Loading…</Typography>
            </Box>
          ) : membersQ.error ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {(membersQ.error as Error).message}
              </Typography>
            </Box>
          ) : !membersQ.data ||
            membersQ.data.items.filter((m) => m.role === "pharmacist")
              .length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                No {memberFilter} pharmacists at this pharmacy.
              </Typography>
            </Box>
          ) : (
            <>
              {membersQ.data.items
                .filter((m) => m.role === "pharmacist")
                .map((m, i, arr) => (
                  <Box key={m.uid}>
                    <MemberRow
                      member={m}
                      chainId={chainId}
                      transferTargets={transferTargets}
                      onChange={invalidate}
                      isSelf={!!user && m.uid === user.uid}
                    />
                    {i < arr.length - 1 && <Divider />}
                  </Box>
                ))}
            </>
          )}
        </Paper>

        <BookingSettingsSection chainId={chainId} pharmacyId={pharmacyId} />

      </Box>

      <AdminDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete pharmacy"
        subtitle="This action is permanent and cannot be undone."
        destructive
      >
        <DeletePharmacyPanel
          chainId={chainId}
          pharmacyId={pharmacyId}
          displayName={String(p.displayName ?? pharmacyId)}
          onCancel={() => setDeleteOpen(false)}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ["chain", chainId, "pharmacy", pharmacyId] });
            qc.invalidateQueries({ queryKey: ["chain", chainId, "pharmacies"] });
            qc.invalidateQueries({ queryKey: ["chain", chainId, "pharmacies", "transfer-targets"] });
            window.location.href = "/pharmacies";
          }}
        />
      </AdminDialog>
    </>
  );
}

function DeletePharmacyPanel({
  chainId,
  pharmacyId,
  displayName,
  onCancel,
  onDeleted,
}: {
  chainId: string;
  pharmacyId: string;
  displayName: string;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [confirm, setConfirm] = useState("");

  const m = useMutation({
    mutationFn: () => chainAdminApi.deletePharmacy({ chainId, pharmacyId }),
    onSuccess: () => {
      showSuccess(`Pharmacy "${displayName}" deleted.`);
      onDeleted();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const ready = confirm.trim() === displayName && !m.isPending;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Alert severity="error">
        This is permanent and cannot be undone. The pharmacy can only be deleted
        when it has no pharmacist memberships and no pending invites. The server
        will reject the request if any of those still exist.
      </Alert>
      <Typography variant="body2">
        Type <strong>{displayName}</strong> to confirm:
      </Typography>
      <TextField
        size="small"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder={displayName}
        disabled={m.isPending}
        fullWidth
      />
      <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
        <Button variant="text" onClick={onCancel} disabled={m.isPending}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={() => m.mutate()} disabled={!ready}>
          {m.isPending ? "Deleting…" : "Permanently delete"}
        </Button>
      </Stack>
    </Box>
  );
}

function PharmacyAdminRow({
  member,
  chainId,
  onChange,
}: {
  member: MembershipRow;
  chainId: string;
  onChange: () => void;
}) {
  const { showSuccess, showError, showWarning } = useToast();
  const { deliverChainKeyToMember, isReady: cryptoReady } = useCrypto();
  const { db } = getFirebaseClient();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState<"revoke" | "suspend" | "reactivate" | null>(null);
  const [reason, setReason] = useState("");
  const isSuspended = member.status === "suspended";

  const memberKeyQ = useQuery({
    queryKey: ["memberKey", chainId, member.uid],
    queryFn: async () => {
      const snap = await getDoc(
        doc(db, "chains", chainId, "memberKeys", member.uid),
      );
      return { exists: snap.exists() };
    },
    enabled: !!chainId && !!member.uid,
    staleTime: 30_000,
  });

  const deliverKeyM = useMutation({
    mutationFn: () =>
      deliverChainKeyToMember(member.uid, chainId, member.pharmacyId),
    onSuccess: (delivered) => {
      if (delivered) {
        showSuccess(`Encryption key re-delivered to ${member.email}`);
        qc.invalidateQueries({ queryKey: ["memberKey", chainId, member.uid] });
      } else {
        showWarning(
          `${member.email}'s device key hasn't synced to the server yet. This usually means they haven't opened the portal, or their last portal sign-in hasn't finished syncing — ask them to sign in again (with a connection) and retry in a few minutes.`,
        );
      }
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Key delivery failed"),
  });

  const revokeM = useMutation({
    mutationFn: () =>
      chainAdminApi.revokeMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
        disableAuth: true,
      }),
    onSuccess: (res) => {
      showSuccess(`Revoked ${member.email}`);
      if (res.authError) showWarning(`Auth disable: ${res.authError}`);
      setConfirming(null);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const suspendM = useMutation({
    mutationFn: () =>
      chainAdminApi.suspendMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Suspended ${member.email}`);
      setConfirming(null);
      setReason("");
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const reactivateM = useMutation({
    mutationFn: () =>
      chainAdminApi.reactivateMembership({ membershipId: member.membershipId }),
    onSuccess: () => {
      showSuccess(`Reactivated ${member.email}`);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        sx={{ alignItems: { md: "flex-start" }, justifyContent: "space-between" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {member.email || "(no email)"}
            </Typography>
            {isSuspended && <Chip label="suspended" size="small" color="warning" />}
            <Chip label="pharmacy admin" size="small" color="secondary" variant="outlined" />
            {memberKeyQ.data?.exists === true ? (
              <Chip label="🔑 E2EE" size="small" color="success" variant="outlined" />
            ) : memberKeyQ.data?.exists === false ? (
              <Chip label="no key" size="small" color="warning" variant="outlined" />
            ) : null}
          </Stack>
          {member.displayName ? (
            <Typography variant="caption" color="text.secondary" component="p">
              {member.displayName}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {member.uid}
          </Typography>
          {isSuspended && member.suspendedReason ? (
            <Typography variant="caption" color="text.secondary" component="p">
              Reason: {member.suspendedReason}
            </Typography>
          ) : null}
        </Box>
        {!confirming ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {!isSuspended && cryptoReady && memberKeyQ.data?.exists === false ? (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => deliverKeyM.mutate()}
                disabled={deliverKeyM.isPending}
              >
                {deliverKeyM.isPending ? "Delivering…" : "Re-deliver key"}
              </Button>
            ) : null}
            {isSuspended ? (
              <Button
                size="small"
                variant="contained"
                onClick={() => setConfirming("reactivate")}
                disabled={reactivateM.isPending}
              >
                Reactivate
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setConfirming("suspend")}
              >
                Suspend
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setConfirming("revoke")}
            >
              Revoke
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {confirming === "reactivate" ? (
        <Box
          sx={{
            mt: 2, p: 2, borderRadius: 1, border: 1,
            borderColor: "primary.light", bgcolor: "primary.50",
          }}
        >
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Reactivate <strong>{member.email}</strong>?
          </Typography>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
            <Button variant="text" size="small" onClick={() => setConfirming(null)}>Cancel</Button>
            <Button
              variant="contained" size="small"
              onClick={() => { reactivateM.mutate(); setConfirming(null); }}
              disabled={reactivateM.isPending}
            >
              {reactivateM.isPending ? "Reactivating…" : "Confirm reactivate"}
            </Button>
          </Stack>
        </Box>
      ) : confirming ? (
        <Box
          sx={{
            mt: 2, p: 2, borderRadius: 1, border: 1,
            borderColor: "error.light", bgcolor: "error.50",
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={confirming === "revoke" ? "Reason (recorded permanently)" : "Reason"}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline rows={2}
              size="small" fullWidth
            />
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button variant="text" size="small" onClick={() => { setConfirming(null); setReason(""); }}>Cancel</Button>
              <Button
                variant="contained" color="error" size="small"
                onClick={() => confirming === "revoke" ? revokeM.mutate() : suspendM.mutate()}
                disabled={revokeM.isPending || suspendM.isPending}
              >
                {confirming === "revoke"
                  ? revokeM.isPending ? "Revoking…" : "Confirm revoke"
                  : suspendM.isPending ? "Suspending…" : "Confirm suspend"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

function MemberRow({
  member,
  chainId,
  transferTargets,
  onChange,
  isSelf,
}: {
  member: MembershipRow;
  chainId: string;
  transferTargets: { id: string; displayName: string }[];
  onChange: () => void;
  isSelf: boolean;
}) {
  const { showSuccess, showError, showWarning } = useToast();
  const { deliverChainKeyToMember, isReady: cryptoReady } = useCrypto();
  const { db } = getFirebaseClient();
  const [confirming, setConfirming] = useState<
    "revoke" | "suspend" | "reactivate" | "transfer" | null
  >(null);
  const [reason, setReason] = useState("");
  const [toPharmacyId, setToPharmacyId] = useState("");

  // Check whether this member already has a chain key slot so we can show
  // an appropriate delivery status badge. Also surfaces whether the key was
  // auto-delivered by the server trigger vs manually delivered by an admin.
  const memberKeyQ = useQuery({
    queryKey: ["memberKey", chainId, member.uid],
    queryFn: async () => {
      const snap = await getDoc(
        doc(db, "chains", chainId, "memberKeys", member.uid),
      );
      if (!snap.exists()) return { exists: false, autoDelivered: false };
      const autoDelivered = snap.data()?.autoDelivered === true;
      return { exists: true, autoDelivered };
    },
    enabled: !!chainId && !!member.uid,
    staleTime: 30_000,
  });

  const qc = useQueryClient();

  const deliverKeyM = useMutation({
    mutationFn: () => deliverChainKeyToMember(member.uid, chainId, member.pharmacyId),
    onSuccess: (delivered) => {
      if (delivered) {
        showSuccess(`E2EE chain key delivered to ${member.email}`);
        qc.invalidateQueries({
          queryKey: ["memberKey", chainId, member.uid],
        });
      } else {
        showWarning(
          `${member.email}'s device key hasn't synced to the server yet. This usually means they haven't opened the app, or their last app open hasn't finished syncing — ask them to open the app again (with a connection) and retry in a few minutes.`,
        );
      }
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Key delivery failed"),
  });
  const isSuspended = member.status === "suspended";

  const revokeM = useMutation({
    mutationFn: () =>
      chainAdminApi.revokeMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
        disableAuth: true,
      }),
    onSuccess: (res) => {
      showSuccess(`Revoked ${member.email}`);
      if (res.authError) showWarning(`Auth disable: ${res.authError}`);
      setConfirming(null);
      onChange();
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Failed"),
  });

  const suspendM = useMutation({
    mutationFn: () =>
      chainAdminApi.suspendMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Suspended ${member.email}`);
      setConfirming(null);
      setReason("");
      onChange();
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Failed"),
  });

  const reactivateM = useMutation({
    mutationFn: () =>
      chainAdminApi.reactivateMembership({
        membershipId: member.membershipId,
      }),
    onSuccess: () => {
      showSuccess(`Reactivated ${member.email}`);
      onChange();
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Failed"),
  });

  const transferM = useMutation({
    mutationFn: () =>
      chainAdminApi.transferMembership({
        membershipId: member.membershipId,
        toChainId: chainId,
        toPharmacyId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Transferred ${member.email}`);
      setConfirming(null);
      setReason("");
      setToPharmacyId("");
      onChange();
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        sx={{ alignItems: { md: "flex-start" }, justifyContent: "space-between" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {member.email || "(no email)"}
            </Typography>
            {isSuspended && (
              <Chip label="suspended" size="small" color="warning" />
            )}
            {/* E2EE key delivery status badge */}
            {memberKeyQ.data?.exists === true ? (
              memberKeyQ.data.autoDelivered ? (
                <Chip label="🔑 auto-delivered" size="small" color="success" variant="outlined" />
              ) : (
                <Chip label="🔑 E2EE" size="small" color="success" variant="outlined" />
              )
            ) : memberKeyQ.data?.exists === false ? (
              <Chip label="no key" size="small" color="default" variant="outlined" />
            ) : null}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {member.uid}
          </Typography>
          {isSuspended && member.suspendedReason ? (
            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
            >
              Reason: {member.suspendedReason}
            </Typography>
          ) : null}
        </Box>
        {!confirming ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {/* Deliver E2EE chain key — shown when no slot exists yet (fallback
                for chains without an escrow copy) or as manual override. */}
            {!isSuspended && cryptoReady && memberKeyQ.data?.exists === false ? (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => deliverKeyM.mutate()}
                disabled={deliverKeyM.isPending}
              >
                {deliverKeyM.isPending ? "Delivering…" : "Deliver E2EE key"}
              </Button>
            ) : null}
            {!isSelf ? (
              <>
                {isSuspended ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setConfirming("reactivate")}
                    disabled={reactivateM.isPending}
                  >
                    Reactivate
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setConfirming("suspend")}
                  >
                    Suspend
                  </Button>
                )}
                {!isSuspended && transferTargets.length > 0 ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setToPharmacyId(transferTargets[0]!.id);
                      setConfirming("transfer");
                    }}
                  >
                    Transfer
                  </Button>
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setConfirming("revoke")}
                >
                  Revoke
                </Button>
              </>
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      {isSelf ? null : confirming === "transfer" ? (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            border: 1,
            borderColor: "primary.light",
            bgcolor: "primary.50",
          }}
        >
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Destination pharmacy (same chain)</InputLabel>
              <Select
                value={toPharmacyId}
                label="Destination pharmacy (same chain)"
                onChange={(e) => setToPharmacyId(e.target.value)}
              >
                {transferTargets.map((pt) => (
                  <MenuItem key={pt.id} value={pt.id}>
                    {pt.displayName} (/{pt.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Reason (recorded on history record)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              placeholder="e.g. Relocated to the city-centre branch"
              size="small"
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Ends this membership and creates a new one at the destination,
              linked back to it. Prior consultations stay rooted at this
              pharmacy.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setConfirming(null);
                  setReason("");
                  setToPharmacyId("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => transferM.mutate()}
                disabled={!toPharmacyId || transferM.isPending}
              >
                {transferM.isPending ? "Transferring…" : "Confirm transfer"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : confirming === "reactivate" ? (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            border: 1,
            borderColor: "primary.light",
            bgcolor: "primary.50",
          }}
        >
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Reactivate <strong>{member.email}</strong>? They will regain access
            to this pharmacy.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setConfirming(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                reactivateM.mutate();
                setConfirming(null);
              }}
              disabled={reactivateM.isPending}
            >
              {reactivateM.isPending
                ? "Reactivating…"
                : "Confirm reactivate"}
            </Button>
          </Stack>
        </Box>
      ) : confirming ? (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            border: 1,
            borderColor: "error.light",
            bgcolor: "error.50",
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={
                confirming === "revoke"
                  ? "Reason (recorded permanently on history record)"
                  : "Reason (recorded on membership; user keeps their account)"
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              placeholder={
                confirming === "revoke"
                  ? "e.g. Left the pharmacy"
                  : "e.g. On extended leave"
              }
              size="small"
              fullWidth
            />
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setConfirming(null);
                  setReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() =>
                  confirming === "revoke"
                    ? revokeM.mutate()
                    : suspendM.mutate()
                }
                disabled={revokeM.isPending || suspendM.isPending}
              >
                {confirming === "revoke"
                  ? revokeM.isPending
                    ? "Revoking…"
                    : "Confirm revoke"
                  : suspendM.isPending
                    ? "Suspending…"
                    : "Confirm suspend"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}


function EditPharmacyForm({
  chainId,
  pharmacyId,
  initial,
  onSuccess,
}: {
  chainId: string;
  pharmacyId: string;
  initial: { name: string; address: string; eircode: string; psi: string };
  onSuccess: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [name, setName] = useState(initial.name);
  const [address, setAddress] = useState(initial.address);
  const [eircode, setEircode] = useState(initial.eircode);
  const [psi, setPsi] = useState(initial.psi);

  const m = useMutation({
    mutationFn: () =>
      chainAdminApi.editPharmacy({
        chainId,
        pharmacyId,
        name: name.trim(),
        address: address.trim(),
        eircode: eircode.trim(),
        psi: psi.trim(),
      }),
    onSuccess: () => {
      showSuccess("Pharmacy updated.");
      onSuccess();
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Edit pharmacy
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <TextField
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Eircode"
          helperText="e.g. D02 XY12"
          value={eircode}
          onChange={(e) => setEircode(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="PSI number"
          helperText="5–6 digits"
          value={psi}
          onChange={(e) => setPsi(e.target.value)}
          size="small"
          fullWidth
        />
      </Box>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={() => m.mutate()}
          disabled={
            !name.trim() ||
            !address.trim() ||
            !eircode.trim() ||
            !psi.trim() ||
            m.isPending
          }
        >
          {m.isPending ? "Saving…" : "Save changes"}
        </Button>
      </Box>
    </Paper>
  );
}

function InvitePharmacistForm({
  chainId,
  pharmacyId,
  onSuccess,
}: {
  chainId: string;
  pharmacyId: string;
  onSuccess: () => void;
}) {
  const { showError } = useToast();
  const { status: cryptoStatus, cryptoRecoveryReason, isReady, reinit, requestRecovery, wrapChainKeyForInvite } = useCrypto();
  const { db } = getFirebaseClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [psiNumber, setPsiNumber] = useState("");
  const psiValid = /^[0-9]{5,6}$/.test(psiNumber.trim());
  const [linkResult, setLinkResult] = useState<
    { email: string; link: string; emailSent: boolean } | null
  >(null);

  const m = useMutation({
    mutationFn: async () => {
      const res = await chainAdminApi.issuePharmacistInvite({
        chainId,
        pharmacyId,
        email: email.trim().toLowerCase(),
        displayName: displayName.trim(),
        psiNumber: psiNumber.trim(),
      });
      // Wrap the chain key under the invite token (passphrase AES-KDF) and
      // persist it on the invite doc. The mobile accept-invite screen reads
      // this and calls storeChainKeyFromToken(chainId, encryptedChainKey, token).
      const encryptedChainKey = await wrapChainKeyForInvite(res.token);
      await setDoc(
        doc(db, "invites", res.inviteId),
        { encryptedChainKey },
        { merge: true },
      );
      return res;
    },
    onSuccess: (res) => {
      // No link is returned when the invitee already has a usable password —
      // the server emails them an access-granted notification instead, and
      // InviteLinkBox renders that case from the empty link. Only treat an
      // empty link as a failure when that email did not go out either.
      if (!res.inviteLink && !res.emailSent) {
        showError(
          "Invite could not be completed — the pharmacist could not be notified. Please try again or contact support.",
        );
        return;
      }
      if (res.inviteLink && !res.emailSent) {
        showError(
          `Account created for ${res.email}, but the credentials email failed to send. Share the link manually.`,
        );
      }
      setLinkResult({ email: res.email, link: res.inviteLink, emailSent: res.emailSent });
      setEmail("");
      setDisplayName("");
      setPsiNumber("");
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Failed"),
  });

  const canSubmit =
    email.trim() && displayName.trim() && psiValid && !m.isPending && isReady;

  const cryptoAlert =
    cryptoStatus === "loading" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Loading encryption key…
      </Alert>
    ) : cryptoStatus === "awaiting-delivery" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Setting up encryption key — this can take up to 60 s…
      </Alert>
    ) : cryptoStatus === "escrow-recovery" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Requesting automatic key recovery…
      </Alert>
    ) : cryptoStatus === "new-device" ? (
      cryptoRecoveryReason === "escrow-missing" ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Recovery unavailable — ask a chain admin who is currently signed in
          to click "Re-deliver key" from this page, then refresh.
        </Alert>
      ) : (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void requestRecovery()}>
              Retry Recovery
            </Button>
          }
        >
          Encryption key could not be recovered automatically. Click Retry
          Recovery to try again, or ask a Katana admin to re-deliver the key.
        </Alert>
      )
    ) : cryptoStatus === "error" ? (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void reinit()}>
            Retry
          </Button>
        }
      >
        Encryption setup failed. Click Retry to try again.
      </Alert>
    ) : cryptoStatus === "no-chain-key" ? (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void reinit()}>
            Retry
          </Button>
        }
      >
        No chain key found. Click Retry, or contact a Katana admin.
      </Alert>
    ) : null;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Add pharmacist
      </Typography>
      {linkResult ? (
        <>
          <InviteLinkBox
            link={linkResult.link}
            email={linkResult.email}
            emailSent={linkResult.emailSent}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button variant="outlined" size="small" onClick={() => { setLinkResult(null); onSuccess(); }}>
              Done
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <TextField
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Full name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="PSI number"
              value={psiNumber}
              onChange={(e) => setPsiNumber(e.target.value)}
              size="small"
              fullWidth
              error={psiNumber.trim().length > 0 && !psiValid}
              helperText={
                psiNumber.trim().length > 0 && !psiValid
                  ? "Enter a 5 or 6 digit PSI registration number."
                  : "5 or 6 digit PSI registration number"
              }
            />
          </Box>
          {cryptoAlert}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={() => m.mutate()}
              disabled={!canSubmit}
            >
              {m.isPending ? "Creating invite…" : "Create invite"}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}

function InvitePharmacyAdminForm({
  chainId,
  pharmacyId,
  onSuccess,
}: {
  chainId: string;
  pharmacyId: string;
  onSuccess: () => void;
}) {
  const { showError } = useToast();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [linkResult, setLinkResult] = useState<
    | { kind: "new"; email: string; link: string; emailSent: boolean }
    | { kind: "existing"; email: string; emailSent: boolean }
    | null
  >(null);

  const m = useMutation({
    mutationFn: () =>
      chainAdminApi.invitePharmacyAdmin({
        chainId,
        pharmacyId,
        email: email.trim().toLowerCase(),
        displayName: displayName.trim(),
      }),
    onSuccess: (res) => {
      // Key off the link, not isNewAccount: an account can exist in Auth
      // without ever having set a password (a leftover from an invite that
      // was never completed), in which case the server still returns a
      // setup link that the admin may need to share manually.
      if (!res.inviteLink) {
        if (!res.emailSent) {
          showError(
            "Invite could not be completed — the invitee could not be notified. Please try again or contact support.",
          );
          return;
        }
        setLinkResult({ kind: "existing", email: res.email, emailSent: res.emailSent });
        setEmail("");
        setDisplayName("");
        return;
      }
      if (!res.emailSent) {
        showError(
          `Account created for ${res.email}, but the credentials email failed to send. Share the link manually.`,
        );
      }
      setLinkResult({ kind: "new", email: res.email, link: res.inviteLink, emailSent: res.emailSent });
      setEmail("");
      setDisplayName("");
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Failed"),
  });

  const canSubmit = email.trim() && displayName.trim() && !m.isPending;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Add pharmacy admin
      </Typography>
      {linkResult ? (
        <>
          {linkResult.kind === "existing" ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Pharmacy admin access granted for <strong>{linkResult.email}</strong>. They can sign in with their existing account.
            </Alert>
          ) : (
            <InviteLinkBox
              link={linkResult.link}
              email={linkResult.email}
              emailSent={linkResult.emailSent}
            />
          )}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button variant="outlined" size="small" onClick={() => { setLinkResult(null); onSuccess(); }}>
              Done
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The account will be created and a password-setup email will be sent automatically.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <TextField
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Full name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={() => m.mutate()}
              disabled={!canSubmit}
            >
              {m.isPending ? "Creating account…" : "Create pharmacy admin"}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
